import React, { useState, useEffect } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

export default function DateOfBirthCertificate() {
  // Session, classes, sections, and students
  const storedSessionId = localStorage.getItem("currentSessionId");
  const [selectedSession] = useState(storedSessionId || "");

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");

  // Certificate fields
  const [studentName, setStudentName] = useState("");
  const [fatherName, setFatherName] = useState("");
  const [studentClass, setStudentClass] = useState("");
  const [section, setSection] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [placeOfBirth, setPlaceOfBirth] = useState("");
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split("T")[0]);
  const [template, setTemplate] = useState("Template1");
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Font customization
  const [headingFontFamily, setHeadingFontFamily] = useState("Times New Roman");
  const [headingFontSize, setHeadingFontSize] = useState(30);
  const [headingFontColor, setHeadingFontColor] = useState("#1e3a8a");
  const [bodyFontFamily, setBodyFontFamily] = useState("Times New Roman");
  const [bodyFontSize, setBodyFontSize] = useState(18);
  const [bodyFontColor, setBodyFontColor] = useState("#000000");
  const [textAlign, setTextAlign] = useState("center");

  const labelStyle = "block font-semibold text-black text-[18px] mb-2";
  const inputStyle =
    "w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-800 text-[14px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

  // ==========================
  // Fetch Classes
  // ==========================
  useEffect(() => {
    if (!selectedSession) return;
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses?sessionId=${selectedSession}`)
      .then((res) => setClasses(res.data))
      .catch((err) => console.error("Error fetching classes:", err));
  }, [selectedSession]);

  // ==========================
  // Fetch Sections by Class
  // ==========================
  useEffect(() => {
    if (!selectedClass) {
      setSections([]);
      setSelectedSection("");
      return;
    }
    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(
          selectedClass
        )}/getSectionsByClassName?sessionId=${selectedSession}`
      )
      .then((res) => setSections(res.data))
      .catch((err) => console.error("Error fetching sections:", err));
  }, [selectedClass, selectedSession]);

  // ==========================
  // Fetch Students by Class & Section
  // ==========================
  useEffect(() => {
    if (!selectedClass || !selectedSection) {
      setStudents([]);
      return;
    }
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/students/getStudents`, {
        params: {
          class: selectedClass,
          section: selectedSection,
          sessionId: selectedSession,
          status: "active",
        },
      })
      .then((res) => setStudents(res.data))
      .catch((err) => console.error("Error fetching students:", err));
  }, [selectedClass, selectedSection, selectedSession]);

  // ==========================
  // Auto-fill student info
  // ==========================
  useEffect(() => {
    if (!selectedStudent) return;
    const stu = students.find((s) => s._id === selectedStudent);
    if (stu) {
      setStudentName(`${stu.firstName} ${stu.lastName}`);
      setFatherName(stu.fatherName || "");
      setStudentClass(stu.class || selectedClass);
      setSection(stu.section || selectedSection);
      setDateOfBirth(stu.dateOfBirth || "");
      setPlaceOfBirth(stu.placeOfBirth || "");
    }
  }, [selectedStudent, students, selectedClass, selectedSection]);

  // ==========================
  // PDF Download
  // ==========================
  const handleDownloadPDF = async () => {
    const certificateElement = document.getElementById("certificate-preview");
    const canvas = await html2canvas(certificateElement, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, imgHeight);
    pdf.save("dob_certificate.pdf");
  };
  const handleSaveCertificate = async () => {
  if (isProcessing) return; 
  setIsProcessing(true)
    try {
      const certificateElement = document.getElementById("certificate-preview");
      const canvas = await html2canvas(certificateElement, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, imgHeight);
      const pdfBlob = pdf.output("blob");
      const file = new File([pdfBlob], "dob_certificate.pdf", { type: "application/pdf" });

      const formData = new FormData();
      formData.append("file", file);
      formData.append("studentId", selectedStudent);
      formData.append("certificateType", "DOB");
      formData.append("sessionId", selectedSession);

      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/certificates/upload`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      console.log("Certificate saved:", res.data);
    alert("Certificate created successfully!");
    } catch (error) {
      console.error("Error saving certificate:", error);
    }
    setIsProcessing(false);
  };

  // ==========================
  // Templates
  // ==========================
  const underlineStyle = {
    borderBottom: "1.5px solid black",
    display: "inline-block",
    minWidth: "140px",
    padding: "0 6px",
    margin: "0 4px",
    fontWeight: "600",
  };

  const Template1 = () => (
    <div
      className="bg-white shadow-xl w-full border-[10px] border-[#2c3e50] rounded-xl text-center max-w-2xl mx-auto p-8"
      style={{ fontFamily: headingFontFamily, textAlign }}
    >
      <h1 className="text-4xl font-bold text-[#2980b9]" style={{ letterSpacing: "3px" }}>
        DATE OF BIRTH CERTIFICATE
      </h1>

      <p className="mt-6 text-lg italic">This is to certify that</p>

      <h2
        className="text-3xl font-semibold mt-3 underline decoration-[#2980b9]"
        style={{ minHeight: "1.5em", textDecorationThickness: "2px" }}
      >
        {studentName || <span style={underlineStyle}>______________________</span>}
      </h2>

      <p className="mt-2 text-lg">
        {studentClass && section ? `Class: ${studentClass}, Section: ${section}` : <span style={underlineStyle}>______________________</span>}
      </p>

      <p className="mt-6 text-lg">S/D of {fatherName || <span style={underlineStyle}>______________________</span>}</p>

      <p className="mt-6 text-lg">was born on</p>
      <h3 className="text-2xl font-semibold">
        {dateOfBirth ? new Date(dateOfBirth).toLocaleDateString() : <span style={underlineStyle}>______________________</span>}
      </h3>

      <p className="mt-6 text-lg">at</p>
      <h3 className="text-2xl font-semibold">
        {placeOfBirth || <span style={underlineStyle}>______________________</span>}
      </h3>

      <p className="mt-8 text-lg">This certificate is issued on</p>
      <h3 className="text-xl font-semibold">{issueDate}</h3>

      <div className="mt-16 text-center">
        <p className="border-t-2 border-gray-700 w-64 mx-auto"></p>
        <p className="mt-2 font-serif font-semibold">Authorized Signature</p>
      </div>
    </div>
  );

  const Template2 = () => (
    <div
      className="p-12 bg-white shadow-lg rounded-xl border-4 border-[#1e3a8a] max-w-2xl mx-auto text-center"
      style={{ fontFamily: bodyFontFamily, textAlign }}
    >
      <h1 className="text-4xl font-bold mb-6" style={{ color: headingFontColor, letterSpacing: "4px" }}>
        DATE OF BIRTH CERTIFICATE
      </h1>

      <p className="text-lg italic mb-4">This certifies that</p>

      <h2 className="text-3xl font-bold underline mb-6" style={{ color: "#f59e0b", textDecorationColor: headingFontColor }}>
        {studentName || <span style={underlineStyle}>______________________</span>}
      </h2>

      <p className="text-lg mb-2">
        {studentClass && section ? `Class: ${studentClass}, Section: ${section}` : <span style={underlineStyle}>______________________</span>}
      </p>

      <p className="text-lg mb-2">S/D of {fatherName || <span style={underlineStyle}>______________________</span>}</p>

      <p className="text-lg mb-2">was born on</p>
      <h3 className="text-2xl font-semibold mb-4">
        {dateOfBirth ? new Date(dateOfBirth).toLocaleDateString() : <span style={underlineStyle}>______________________</span>}
      </h3>

      <p className="text-lg mb-2">at</p>
      <h3 className="text-2xl font-semibold mb-8">
        {placeOfBirth || <span style={underlineStyle}>______________________</span>}
      </h3>

      <p className="text-lg mb-10">Issued on: <span className="font-semibold">{issueDate}</span></p>

      <div className="mt-12">
        <p className="border-t-2 border-gray-700 w-60 mx-auto"></p>
        <p className="mt-2 font-semibold">Authorized Signature</p>
      </div>
    </div>
  );

  const Template3 = () => (
    <div
      className="p-10 bg-white shadow-lg rounded-xl border-8 border-[#f39c12] max-w-2xl mx-auto text-center"
      style={{ fontFamily: "'Palatino Linotype', serif", textAlign }}
    >
      <h1 className="text-4xl font-bold text-gray-800 uppercase mb-5">
        Date of Birth Certificate
      </h1>

      <p className="italic mb-2 text-gray-600 tracking-wide">This certifies that</p>

      <h2 className="text-4xl font-extrabold mb-6" style={{ color: "#a16b3d" }}>
        {studentName || <span style={underlineStyle}>______________________</span>}
      </h2>

      <p className="mb-2">
        {studentClass && section ? `Class: ${studentClass}, Section: ${section}` : <span style={underlineStyle}>______________________</span>}
      </p>

      <p className="mb-2">S/D of {fatherName || <span style={underlineStyle}>______________________</span>}</p>

      <p className="mb-2">was born on</p>
      <h3 className="text-3xl font-semibold mb-5">
        {dateOfBirth ? new Date(dateOfBirth).toLocaleDateString() : <span style={underlineStyle}>______________________</span>}
      </h3>

      <p className="mb-2">at</p>
      <h3 className="text-3xl font-semibold mb-10">
        {placeOfBirth || <span style={underlineStyle}>______________________</span>}
      </h3>

      <p className="mb-8">Issued on: <span className="font-semibold">{issueDate}</span></p>

      <div className="mt-12">
        <p className="border-t-2 border-gray-700 w-48 mx-auto"></p>
        <p className="mt-2 font-semibold">Authorized Signature</p>
      </div>
    </div>
  );

  // ==========================
  // JSX
  // ==========================
  return (
    <div className="p-6 bg-white shadow-md rounded-lg w-full max-w-screen-xl mx-auto">
      {/* Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Class */}
        <div>
          <label className={labelStyle}>Class</label>
          <select
            className={inputStyle}
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
          >
            <option value="">Select Class</option>
            {classes.map((cls) => (
              <option key={cls._id} value={cls.className}>
                {cls.className}
              </option>
            ))}
          </select>
        </div>

        {/* Section */}
        <div>
          <label className={labelStyle}>Section</label>
          <select
            className={inputStyle}
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
          >
            <option value="">Select Section</option>
            {sections.map((sec) => (
              <option key={sec._id} value={sec.sectionName}>
                {sec.sectionName}
              </option>
            ))}
          </select>
        </div>

        {/* Student */}
        <div className="md:col-span-2">
          <label className={labelStyle}>Student</label>
          <select
            className={inputStyle}
            value={selectedStudent}
            onChange={(e) => setSelectedStudent(e.target.value)}
          >
            <option value="">Select Student</option>
            {students.map((stu) => (
              <option key={stu._id} value={stu._id}>
                {stu.firstName} {stu.lastName}
              </option>
            ))}
          </select>
        </div>

        {/* Date of Birth */}
        <div>
          <label className={labelStyle}>Date of Birth</label>
          <input
            type="date"
            className={inputStyle}
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
          />
        </div>
        {/* Place of Birth */}
        <div>
          <label className={labelStyle}>Place of Birth</label>
          <input
            className={inputStyle}
            value={placeOfBirth}
            onChange={(e) => setPlaceOfBirth(e.target.value)}
            placeholder="City / Town"
          />
        </div>

        {/* Issue Date */}
        <div>
          <label className={labelStyle}>Issue Date</label>
          <input
            type="date"
            className={inputStyle}
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
          />
        </div>

        {/* Template */}
        <div className="md:col-span-2">
          <label className={labelStyle}>Select Template</label>
          <select
            className={inputStyle}
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
          >
            <option value="Template1">Template 1</option>
            <option value="Template2">Template 2</option>
            <option value="Template3">Template 3</option>
          </select>
        </div>
      </div>

      {/* Certificate Preview */}
      <div id="certificate-preview" className="mt-6">
        {template === "Template1" && <Template1 />}
        {template === "Template2" && <Template2 />}
        {template === "Template3" && <Template3 />}
      </div>

      {/* Buttons */}
       <div className="flex flex-wrap gap-4 mt-6">
        <button onClick={handleDownloadPDF} className="px-8 py-3 bg-green-600 text-white font-semibold rounded-lg shadow-lg hover:bg-green-700">Download PDF</button>
        <button onClick={handleSaveCertificate} className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow-lg hover:bg-blue-700">Create Certificate</button>
      </div>
    </div>
  );
}
